import React, { useEffect, useState, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { getDynamicLogoPath } from "../../utils/functions";
import frontend from "../../../../config/frontend";

const Last10GamesChartForBild = ({
  competitionId,
  homeTeam,
  awayTeam,
  standings,
  teamColors,
  week,
  totalStandingTeams,
  startChartAnimation,
}) => {
  const [animationStep, setAnimationStep] = useState(0);

  const homeTeamId = homeTeam.id;
  const awayTeamId = awayTeam.id;

  const totalTeams = totalStandingTeams || 18;
  const weekNumber = parseInt(week, 10) || 26;

  // Detect winner: lowest cumulative position across the run. Before any
  // matchday has been played there is nothing to compare, and reducing
  // without a seed throws on an empty list — leaving winner undefined just
  // means neither team gets the highlight, which is what an empty chart wants.
  const positionTotals = Object.entries(
    (standings || []).reduce((acc, obj) => {
      for (const key in obj) acc[key] = (acc[key] || 0) + obj[key];
      return acc;
    }, {})
  );

  const winner = positionTotals.length
    ? positionTotals.reduce((min, curr) => (curr[1] < min[1] ? curr : min))[0]
    : undefined;

  const yTicks = Array.from({ length: totalTeams }, (_, i) => i + 1);

  // Valid team detection
  const validTeams = standings.reduce((acc, match) => {
    Object.keys(match).forEach((teamId) => {
      if (match[teamId] !== null && match[teamId] !== undefined) {
        acc.add(teamId);
      }
    });
    return acc;
  }, new Set());

  const isHomeTeamValid = validTeams.has(homeTeamId);
  const isAwayTeamValid = validTeams.has(awayTeamId);

  // Build filtered standings (same as before, just moved up)
  let filteredStandings = [];
  if (weekNumber < 10) {
    const startIndex = standings.length - weekNumber;
    filteredStandings = Array.from({ length: 10 }, (_, index) => {
      if (index >= weekNumber) {
        return { matchday: index + 1 };
      }
      const actualIndex = index + startIndex;
      const match = standings[actualIndex];
      return {
        matchday: index + 1,
        ...(isHomeTeamValid && { [homeTeamId]: match?.[homeTeamId] - 0.5 }),
        ...(isAwayTeamValid && { [awayTeamId]: match?.[awayTeamId] - 0.5 }),
      };
    });
  } else {
    filteredStandings = standings.map((match, index) => ({
      matchday: index + 1,
      ...(isHomeTeamValid && { [homeTeamId]: match[homeTeamId] - 0.5 }),
      ...(isAwayTeamValid && { [awayTeamId]: match[awayTeamId] - 0.5 }),
    }));
  }

  const totalSteps = filteredStandings.length * 2;

  // X-Axis labels
  const generateXAxisLabels = (weekNumber) => {
    if (weekNumber < 10) {
      return Array.from({ length: 10 }, (_, i) => i + 1);
    }
    return Array.from({ length: 10 }, (_, i) => weekNumber - 9 + i);
  };

  const xAxisLabels = generateXAxisLabels(weekNumber);

  // ----------------------------
  // Custom Dot Component
  // ----------------------------
  const CustomDot = ({
    cx,
    cy,
    index,
    fill,
    stroke,
    team,
    competitionId,
    animationStep,
    data,
  }) => {
    const isBild = frontend.client === "bild_live";
    const src = isBild
      ? `https://s.hs-data.com/gfx/emblem/common/200x200/${parseInt(
        team?.id
      )}.png`
      : getDynamicLogoPath({ team, competitionId });

    const hasValidData = (match) =>
      Object.keys(match).some((key) => key !== "matchday");

    let isLastPoint = false;

    if (!data || data.length === 0) return null;

    if (weekNumber < 10) {
      const lastValidIndex = data
        .map((match, idx) => (hasValidData(match) ? idx : -1))
        .filter((idx) => idx !== -1)
        .pop();
      if (!hasValidData(data[index])) return null;
      isLastPoint = index === lastValidIndex;
    } else {
      isLastPoint = index === standings.length - 1;
    }

    // Dot animation linked to animationStep
    if (index > Math.floor(animationStep / 2)) return null;

    return (
      <g>
        {!isLastPoint ? (
          <circle cx={cx} cy={cy} r={8} fill={fill} stroke={stroke} strokeWidth={3} />
        ) : (
          <image href={src} x={cx - 30} y={cy - 30} width={60} height={60} />
        )}
      </g>
    );
  };

  // ----------------------------
  // Animated data: only reveal line up to current visible index
  // ----------------------------
  const animatedStandings = useMemo(() => {
    const maxIndex = Math.floor(animationStep / 2);

    return filteredStandings.map((entry, idx) => {
      // everything before or equal to maxIndex is visible
      if (idx <= maxIndex) return entry;

      // hide the values for both teams after maxIndex
      const { matchday } = entry;
      const hidden = { matchday };

      if (isHomeTeamValid) hidden[homeTeamId] = null;
      if (isAwayTeamValid) hidden[awayTeamId] = null;

      return hidden;
    });
  }, [
    filteredStandings,
    animationStep,
    isHomeTeamValid,
    isAwayTeamValid,
    homeTeamId,
    awayTeamId,
  ]);

  // ----------------------------
  // Start animation interval
  // ----------------------------
  useEffect(() => {
    if (!startChartAnimation) return;

    setAnimationStep(0); // reset each time animation starts

    const interval = setInterval(() => {
      setAnimationStep((prev) => {
        if (prev < totalSteps) return prev + 1;
        clearInterval(interval);
        return prev;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [startChartAnimation, totalSteps]);


  const homePosition =
    isHomeTeamValid && standings.length
      ? standings[standings.length - 1]?.[homeTeamId]
      : null;

  const awayPosition =
    isAwayTeamValid && standings.length
      ? standings[standings.length - 1]?.[awayTeamId]
      : null;

  // ----------------------------
  // Render Chart
  // ----------------------------
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <ResponsiveContainer width="100%" height={800}>
        <LineChart
          data={animatedStandings}
          margin={{ top: 50, right: 30, left: 10, bottom: 20 }}
        >
          <CartesianGrid vertical={false} stroke="rgb(207, 212, 218)" strokeWidth={2} />

          <XAxis
            stroke="rgb(207, 212, 218)"
            strokeWidth={2}
            orientation="top"
            dataKey="matchday"
            tickFormatter={(value, index) => xAxisLabels[index]}
            tick={{ fill: "rgb(43, 64, 93)", fontSize: 16 }}
            padding={{ left: 40, right: 40 }}
            label={{
              value: "Spieltag",
              position: "top",
              offset: 20,
              fill: "black",
              fontSize: 22,
            }}
          />

          <YAxis
            stroke="#fff"
            domain={[0, totalTeams]}
            ticks={yTicks}
            tick={({ payload, x }) => {
              const tickSpacing =
                yTicks.length > 1
                  ? Math.abs(yTicks[1] - yTicks[0])
                  : totalTeams;
              const halfSpacing = tickSpacing / 2;
              let fill = "rgb(43, 64, 93)";
              const value = payload.value;
              if (value === homePosition && teamColors?.home) {
                fill = teamColors.home;
              }
              if (value === awayPosition && teamColors?.away) {
                // if both teams share same position, away color will override;
                // tweak logic if you want blending or priority instead
                fill = teamColors.away;
              }

              return (
                <g transform={`translate(${x},${payload.coordinate})`}>
                  <rect x={-32} y={-32} width={49} height={34} fill="none" />
                  <line
                    x1={-32}
                    x2={18}
                    y1={halfSpacing}
                    y2={halfSpacing}
                    stroke="rgb(207, 212, 218)"
                    strokeWidth={2}
                  />
                  <text
                    x={-5}
                    y={1}
                    dy={-12}
                    fill={fill}
                    fontSize={16}
                    textAnchor="middle"
                  >
                    {payload.value + "."}
                  </text>
                </g>
              );
            }}
            axisLine={{ stroke: "#1c3042", strokeWidth: 0 }}
            tickLine={false}
            reversed={true}
            tickMargin={10}
            label={({ viewBox }) => {
              const { x, y } = viewBox;
              return (
                <g transform={`translate(${x + 30}, ${y - 20})`}>
                  <text
                    x={0}
                    y={0}
                    fill="black"
                    fontSize={22}
                    textAnchor="middle"
                  >
                    Pos.
                  </text>
                  {/* Sticky Line Under "Pos" */}
                  <line
                    x1={-18}
                    x2={30}
                    y1={20}
                    y2={20}
                    stroke="rgb(207, 212, 218)"
                    strokeWidth={2}
                  />
                </g>
              );
            }}
          />

          {/* ---------------- HOME TEAM ---------------- */}
          {isHomeTeamValid && (
            <>
              {/* background thick white line */}
              <Line
                type="monotone"
                dataKey={homeTeamId}
                stroke="white"
                strokeWidth={25}
                strokeOpacity={0.3}
                dot={false}
                isAnimationActive={false}
              />

              {/* colored actual line */}
              <Line
                type="monotone"
                dataKey={homeTeamId}
                stroke={teamColors?.home}
                strokeWidth={6}
                dot={(props) => (
                  <CustomDot
                    {...props}
                    fill={winner === homeTeamId ? teamColors?.home : "#597884"}
                    stroke={
                      winner === homeTeamId ? "transparent" : teamColors?.home
                    }
                    team={homeTeam}
                    competitionId={competitionId}
                    animationStep={animationStep}
                    data={animatedStandings}
                  />
                )}
                isAnimationActive={false}
              />
            </>
          )}

          {/* ---------------- AWAY TEAM ---------------- */}
          {isAwayTeamValid && (
            <>
              {/* background white line */}
              <Line
                type="monotone"
                dataKey={awayTeamId}
                stroke="white"
                strokeWidth={25}
                strokeOpacity={0.3}
                dot={false}
                isAnimationActive={false}
              />

              {/* colored actual line */}
              <Line
                type="monotone"
                dataKey={awayTeamId}
                stroke={teamColors?.away}
                strokeWidth={6}
                dot={(props) => (
                  <CustomDot
                    {...props}
                    fill={winner === awayTeamId ? teamColors?.away : "#597884"}
                    stroke={
                      winner === awayTeamId ? "transparent" : teamColors?.away
                    }
                    team={awayTeam}
                    competitionId={competitionId}
                    animationStep={animationStep}
                    data={animatedStandings}
                  />
                )}
                isAnimationActive={false}
              />
            </>
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default Last10GamesChartForBild;
